/**
 * AereoPage.js — Estação de trabalho de busca de voos
 * Visualização: card único ida+volta, igual ao voos.dsctravel.com.br
 */

import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plane, ArrowLeftRight, Search,
  CheckCircle, AlertCircle, Loader2, Luggage, Package
} from 'lucide-react'
import Sidebar from '../components/Sidebar'
import { colors } from '../styles/tokens'
import { getAirline } from '../config/airlines'
import { api } from '../services/api'
import BookingModal from '../components/BookingModal'
import BookingDetail from '../components/BookingDetail'

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'

// ─── Utilitários ──────────────────────────────────────────────────────────────

function formatTime(datetimeStr) {
  if (!datetimeStr) return '--:--'
  return new Date(datetimeStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function formatDateShort(datetimeStr) {
  if (!datetimeStr) return ''
  return new Date(datetimeStr + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '')
}

function formatDuration(minutes) {
  if (!minutes) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}h${m > 0 ? String(m).padStart(2, '0') + 'm' : ''}`
}

function formatBRL(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
}

// ─── AirportInput ─────────────────────────────────────────────────────────────

function AirportInput({ value, onChange, onCityChange, placeholder }) {
  const [query, setQuery] = useState(value)
  const mountedRef = useRef(false)
  useEffect(() => {
    if (!mountedRef.current && value && value.length === 3) {
      onChange(value)
      mountedRef.current = true
    }
  }, [])
  const [suggestions, setSuggestions] = useState([])
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [loading, setLoading] = useState(false)
  const requestIdRef = useRef(0)
  const debounceRef = useRef(null)
  const ref = useRef(null)

  const close = useCallback(() => { setOpen(false); setSuggestions([]); setActiveIndex(-1) }, [])
  const cleanCity = (city) => city ? city.split(' - ')[0].trim() : city
  const pick = useCallback((code, city) => { setQuery(code); onChange(code); if (onCityChange && city) onCityChange(cleanCity(city)); close() }, [onChange, onCityChange, close])

  const doQuery = useCallback(async (q) => {
    const myId = ++requestIdRef.current
    setLoading(true)
    try {
      const r = await fetch(`${API_URL}/public/airports/search?q=${encodeURIComponent(q)}&limit=6`)
      if (!r.ok) { close(); return }
      const data = await r.json()
      if (myId !== requestIdRef.current) return
      setSuggestions(data.airports || [])
      setOpen(true)
      setActiveIndex(-1)
    } catch { close() }
    finally { setLoading(false) }
  }, [close])

  const handleInput = (e) => {
    const val = e.target.value.toUpperCase().slice(0, 20)
    setQuery(val)
    if (val.length === 0) { onChange(''); return }
    if (val.length === 3 && /^[A-Z]{3}$/.test(val)) {
      onChange(val)
      // Resolve cidade pelo endpoint para não perder o nome
      fetch(`${API_URL}/public/airports/search?q=${val}&limit=1`)
        .then(r => r.json())
        .then(data => {
          const ap = data.airports?.[0]
          if (ap && ap.code === val && onCityChange && ap.city) onCityChange(ap.city.split(' - ')[0].trim())
        })
        .catch(() => {})
      close()
      return
    }
    clearTimeout(debounceRef.current)
    if (val.length < 2) { close(); return }
    debounceRef.current = setTimeout(() => doQuery(val), 250)
  }

  const handleKeyDown = (e) => {
    if (!open || !suggestions.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => (i + 1) % suggestions.length) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => i <= 0 ? suggestions.length - 1 : i - 1) }
    else if (e.key === 'Enter' && activeIndex >= 0) { e.preventDefault(); pick(suggestions[activeIndex].code, suggestions[activeIndex].city) }
    else if (e.key === 'Escape') { close() }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        value={query}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={e => { e.target.select(); e.target.style.borderColor = colors.accent; if (suggestions.length) setOpen(true) }}
        onBlur={e => { e.target.style.borderColor = '#e2e8f0'; setTimeout(close, 120) }}
        placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', fontSize: '14px', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', color: '#0f172a', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
      />
      {loading && <Loader2 size={14} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', animation: 'spin 1s linear infinite', color: '#94a3b8' }} />}
      {open && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: '4px', overflow: 'hidden' }}>
          {suggestions.map((ap, i) => (
            <div key={ap.code} onMouseDown={e => { e.preventDefault(); pick(ap.code, ap.city) }}
              style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid #f1f5f9', background: i === activeIndex ? '#f8fafc' : 'white' }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '14px', color: colors.primary, minWidth: '36px' }}>{ap.code}</span>
              <div>
                <div style={{ fontSize: '13px', color: '#0f172a' }}>{ap.city}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{ap.name}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── AirlineLogo ──────────────────────────────────────────────────────────────

function AirlineLogo({ code, name, size = 28 }) {
  const airline = getAirline(code) || getAirline(name)
  if (airline?.logo) {
    return <img src={airline.logo} alt={airline.name} style={{ width: size, height: size, objectFit: 'contain', borderRadius: '4px' }} onError={e => { e.target.style.display = 'none' }} />
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: colors.primary }}>
      {(code || '?').slice(0, 2)}
    </div>
  )
}

// ─── ItineraryBlock — tabela padrão Rextur ───────────────────────────────────

// Colunas: cia | voo | saída | chegada | origem | destino | esc | equip. | tipo | classe | família | bagagem | total
// Total só na última linha do card (passado como prop)
const GRID = '28px 58px 52px 52px 42px 110px 24px 36px 28px 48px 52px 68px 80px'

const HDR = { fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'lowercase', letterSpacing: '0.04em' }
const CELL = { fontSize: '11px', color: '#1e293b', display: 'flex', alignItems: 'center' }

function ItineraryBlock({ itin, showTotal, price, baggage, fareFamily }) {
  if (!itin) return null
  const segs = itin.segments || []

  const calcConnection = (seg, nextSeg) => {
    const arr = seg.arrivalDateTime
    const dep = nextSeg.departureDateTime
    if (!arr || !dep) return ''
    const diff = (new Date(dep) - new Date(arr)) / 60000
    if (diff <= 0) return ''
    const h = Math.floor(diff / 60)
    const m = diff % 60
    return h > 0 ? `${h}h${m > 0 ? m + 'min' : ''}` : `${m}min`
  }

  const carryOn = baggage?.carryOnIncluded
  const checked = baggage?.checkedIncluded

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      {/* Cabeçalho cinza */}
      <div style={{
        display: 'grid', gridTemplateColumns: GRID,
        background: '#f1f5f9', padding: '3px 8px',
        borderRadius: '4px 4px 0 0', gap: '4px', alignItems: 'center',
      }}>
        {['cia','voo','saída','chegada','origem','destino(s)','esc','equip.','tipo','classe','família','bagagem','total'].map(h => (
          <span key={h} style={HDR}>{h}</span>
        ))}
      </div>

      {/* Linhas dos segmentos */}
      {segs.map((seg, i) => {
        const airportChange = seg.airport_change
        const conn = i < segs.length - 1 ? calcConnection(seg, segs[i + 1]) : ''
        const isLast = i === segs.length - 1

        return (
          <div key={i}>
            <div style={{
              display: 'grid', gridTemplateColumns: GRID,
              padding: '2px 8px', gap: '4px', alignItems: 'center',
              height: '20px',
              background: i % 2 === 0 ? 'white' : '#fafafa',
              borderBottom: '1px solid #f1f5f9',
            }}>
              {/* CIA */}
              <AirlineLogo code={seg.airline?.code} name={seg.airline?.name} size={18} />
              {/* VOO */}
              <span style={{ ...CELL, fontFamily: 'monospace', fontWeight: 700, color: colors.primary, fontSize: '11px' }}>
                {seg.airline?.code}{seg.flightNumber}
              </span>
              {/* SAÍDA */}
              <span style={{ ...CELL, fontFamily: 'monospace', fontWeight: 600 }}>
                {formatTime(seg.departureDateTime)}
              </span>
              {/* CHEGADA */}
              <span style={{ ...CELL, fontFamily: 'monospace', fontWeight: 600 }}>
                {formatTime(seg.arrivalDateTime)}
              </span>
              {/* ORIGEM */}
              <span style={{ ...CELL, fontWeight: 600 }}>{seg.origin?.code}</span>
              {/* DESTINO */}
              <span style={{ ...CELL, fontWeight: 600 }}>{seg.destination?.code}</span>
              {/* ESC */}
              <span style={{ ...CELL, justifyContent: 'center' }}>
                {isLast ? (itin.stops || 0) : ''}
              </span>
              {/* EQUIP */}
              <span style={{ ...CELL }}>{seg.aircraftCode || ''}</span>
              {/* TIPO */}
              <span style={{ ...CELL }}>OW</span>
              {/* CLASSE */}
              <span style={{ ...CELL }}>{seg.cabinClass || 'economy'}</span>
              {/* FAMÍLIA */}
              <span style={{ ...CELL, fontSize: '10px' }}>
                {isLast ? (fareFamily || '') : ''}
              </span>
              {/* BAGAGEM */}
              <span style={{ ...CELL, gap: '4px', fontSize: '10px' }}>
                {isLast ? (
                  <>
                    <Package size={11} color={carryOn ? '#059669' : '#cbd5e1'} />
                    <Luggage size={11} color={checked ? '#059669' : '#cbd5e1'} />
                  </>
                ) : ''}
              </span>
              {/* TOTAL */}
              <span style={{ ...CELL, justifyContent: 'flex-end', fontWeight: 700, color: colors.primary, fontSize: '12px' }}>
                {isLast && showTotal ? formatBRL(price) : ''}
              </span>
            </div>

            {/* Badge conexão */}
            {i < segs.length - 1 && (
              <div style={{
                padding: '2px 8px',
                background: airportChange ? '#fef2f2' : '#fffbeb',
                fontSize: '10px', fontWeight: 600,
                color: airportChange ? '#dc2626' : '#d97706',
                display: 'flex', alignItems: 'center', gap: '5px',
              }}>
                {airportChange
                  ? <span>⚠️ Troca de aeroporto · {seg.airport_change_detail}</span>
                  : <span>Conexão em {seg.destination?.code}</span>
                }
                {conn && <span style={{ color: '#94a3b8', fontWeight: 400 }}>· {conn} de espera</span>}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}


// ─── OfferCard — card único com ida + volta ───────────────────────────────────

function OfferCard({ offer, selected, onSelect }) {
  const isRoundTrip = !!(offer.return)

  return (
    <div
      onClick={() => onSelect(offer)}
      style={{
        border: selected ? `2px solid ${colors.accent}` : '1px solid #e2e8f0',
        borderRadius: '6px', padding: '6px',
        cursor: 'pointer',
        background: selected ? 'rgba(80,207,173,0.04)' : 'white',
        marginBottom: '6px', transition: 'all 0.12s',
        boxShadow: selected ? `0 0 0 3px rgba(80,207,173,0.12)` : '0 1px 2px rgba(0,0,0,0.04)'
      }}
    >
      {selected && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <CheckCircle size={12} color={colors.accent} />
          <span style={{ fontSize: '10px', fontWeight: 600, color: colors.accent }}>Selecionado</span>
        </div>
      )}

      <ItineraryBlock
        itin={offer.outbound}
        showTotal={!isRoundTrip}
        price={offer.price?.total}
        baggage={offer.baggage}
        fareFamily={offer.baggage?.fareFamily || offer.outbound?.segments?.[0]?.fareFamily}
      />

      {isRoundTrip && offer.return && (
        <div style={{ marginTop: '2px' }}>
          <ItineraryBlock
            itin={offer.return}
            showTotal={true}
            price={offer.price?.total}
            baggage={offer.baggage}
            fareFamily={offer.baggage?.fareFamily || offer.return?.segments?.[0]?.fareFamily}
          />
        </div>
      )}
    </div>
  )
}


// ─── FlightCart — carrinho múltiplo ───────────────────────────────────────────

function FlightCart({ cart, adults, onRemove, onMontar, mounting, onReservar, roundTrip }) {
  const outbound = cart.outbound || []
  const ret      = cart.return   || []
  const hasAny   = outbound.length > 0 || ret.length > 0

  if (!hasAny) return null

  const basePrice = outbound[0]?.price?.total || 0

  // Regras dos botões
  const onlyOutbound   = !roundTrip || ret.length === 0
  const waitingReturn  = roundTrip && outbound.length > 0 && ret.length === 0
  const multipleInAny  = outbound.length > 1 || ret.length > 1
  const showReservar   = !multipleInAny && !waitingReturn && outbound.length === 1 && (onlyOutbound || ret.length === 1)
  const mainLabel      = waitingReturn ? 'Ver voo de volta' : 'Montar Cotação'
  const canMontar      = !waitingReturn && outbound.length > 0

  const CartItem = ({ offer, label, index, onDel }) => {
    const segs = offer.outbound?.segments || []
    const first = segs[0] || {}
    const last  = segs[segs.length - 1] || first
    const diffPrice = offer.price?.total - basePrice
    const diffStr = index === 0 ? 'base' : diffPrice >= 0 ? `+${formatBRL(diffPrice)}` : formatBRL(diffPrice)
    const diffColor = index === 0 ? colors.accent : diffPrice > 0 ? '#d97706' : '#059669'

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#0f172a' }}>
            {first.origin?.code || offer.outbound?.originCode} → {last.destination?.code || offer.outbound?.destinationCode}
          </span>
          <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '6px' }}>
            {formatTime(first.departureDateTime || offer.outbound?.departureDateTime)}
          </span>
          {index === 0 && <span style={{ fontSize: '10px', fontWeight: 700, color: diffColor, marginLeft: '6px' }}>BASE</span>}
        </div>
        <span style={{ fontSize: '12px', fontWeight: 700, color: diffColor }}>{diffStr}</span>
        <button onClick={() => onDel(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '14px', padding: '0 4px' }}>✕</button>
      </div>
    )
  }

  return (
    <div style={{ background: 'white', border: `1.5px solid ${colors.accent}`, borderRadius: '10px', padding: '14px 18px', marginBottom: '20px' }}>
      <div style={{ fontSize: '10px', fontWeight: 700, color: colors.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
        Carrinho · {outbound.length + ret.length} voo{outbound.length + ret.length !== 1 ? 's' : ''}
      </div>

      {outbound.length > 0 && (
        <div style={{ marginBottom: ret.length > 0 ? '10px' : 0 }}>
          <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Ida</div>
          {outbound.map((offer, i) => (
            <CartItem key={offer.offerId || i} offer={offer} label="ida" index={i}
              onDel={idx => onRemove('outbound', idx)} />
          ))}
        </div>
      )}

      {ret.length > 0 && (
        <div>
          <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Volta</div>
          {ret.map((offer, i) => (
            <CartItem key={offer.offerId || i} offer={offer} label="volta" index={i}
              onDel={idx => onRemove('return', idx)} />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
        <div style={{ fontSize: '18px', fontWeight: 700, color: colors.primary }}>
          {formatBRL(basePrice)}
          <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400, marginLeft: '4px' }}>base · {adults} pax</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {showReservar && (
            <button onClick={onReservar}
              style={{ padding: '7px 14px', border: 'none', borderRadius: '8px', background: '#059669', color: 'white', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Plane size={12} /> Reservar
            </button>
          )}
          <button disabled={mounting || !canMontar} onClick={onMontar}
            style={{ padding: '7px 18px', border: 'none', borderRadius: '8px', background: canMontar ? colors.primary : '#e2e8f0', color: canMontar ? 'white' : '#94a3b8', fontSize: '13px', fontWeight: 700, cursor: canMontar && !mounting ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {mounting ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Plane size={13} />}
            {mounting ? 'Criando...' : mainLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export default function AereoPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const contactId = searchParams.get('contact_id') || null
  const contactName = searchParams.get('contact_name') || null

  const [origin, setOrigin] = useState('VDC')
  const [destination, setDestination] = useState('')
  const [originCity, setOriginCity] = useState('Vitória da Conquista')
  const [destinationCity, setDestinationCity] = useState('')
  const [departureDate, setDepartureDate] = useState('')
  const [returnDate, setReturnDate] = useState('')
  const [adults, setAdults] = useState(1)
  const [children, setChildren] = useState(0)
  const [infants, setInfants] = useState(0)
  const [roundTrip, setRoundTrip] = useState(true)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [offers, setOffers] = useState([])
  const [searched, setSearched] = useState(false)
  const [cart, setCart] = useState({ outbound: [], return: [] })
  const [mountingQuote, setMountingQuote] = useState(false)
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [showBookings, setShowBookings] = useState(false)
  const [bookings, setBookings] = useState([])
  const [loadingBookings, setLoadingBookings] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)

  // Oferta base para reserva (primeiro voo de ida)
  const selectedOffer = cart.outbound[0] || null

  const handleAddToCart = useCallback((offer) => {
    setCart(prev => {
      // Determina se é voo de ida ou volta pelo contexto da busca
      // Se roundTrip e já tem outbound completo, vai para return
      const isReturn = roundTrip && prev.outbound.length > 0 &&
        (offer.outbound?.originCode === destination || offer.outbound?.segments?.[0]?.origin?.code === destination)
      const tranche = isReturn ? 'return' : 'outbound'
      // Máximo 3 por trecho
      if (prev[tranche].length >= 3) return prev
      // Não adiciona duplicado
      if (prev[tranche].some(o => o.offerId === offer.offerId)) return prev
      return { ...prev, [tranche]: [...prev[tranche], offer] }
    })
  }, [roundTrip, destination])

  const handleRemoveFromCart = useCallback((tranche, idx) => {
    setCart(prev => ({ ...prev, [tranche]: prev[tranche].filter((_, i) => i !== idx) }))
  }, [])

  const canSearch = origin.length === 3 && destination.length === 3 &&
    departureDate && (roundTrip ? returnDate : true)

  const handleMontarCotacao = useCallback(async () => {
    const outbound = cart.outbound
    const ret      = cart.return
    if (outbound.length === 0) return

    setMountingQuote(true)
    try {
      const buildFlightBlock = (offer, direction) => {
        const itin = direction === 'ida' ? offer.outbound : (offer.return || offer.outbound)
        const segs = itin?.segments || []
        const first = segs[0] || {}
        const last  = segs[segs.length - 1] || {}
        return {
          type: 'flight',
          price: offer.price?.total || 0,
          details: {
            direction: direction === 'ida' ? 'outbound' : 'return',
            origin: first.origin?.code || itin?.originCode || '',
            destination: last.destination?.code || itin?.destinationCode || '',
            departure_date: first.departureDateTime ? first.departureDateTime.split('T')[0] : '',
            departure_time: first.departureDateTime ? first.departureDateTime.split('T')[1]?.slice(0, 5) : '',
            arrival_date: last.arrivalDateTime ? last.arrivalDateTime.split('T')[0] : '',
            arrival_time: last.arrivalDateTime ? last.arrivalDateTime.split('T')[1]?.slice(0, 5) : '',
            adults, children, infants,
            fare_family: offer.fareType || 'ECONOMY',
            is_direct: itin?.isDirect || false,
            baggage: { carry_on: offer.baggage?.carryOnIncluded || false, checked_bags: offer.baggage?.checkedIncluded ? 1 : 0 },
            segments: segs.map((seg, idx) => {
              const nextSeg = segs[idx + 1]
              const airportChange = nextSeg
                ? (seg.destination?.code && nextSeg.origin?.code && seg.destination.code !== nextSeg.origin.code)
                : false
              return {
                origin: seg.origin?.code || '',
                origin_city: seg.origin?.name || '',
                destination: seg.destination?.code || '',
                destination_city: seg.destination?.name || '',
                departure_date: seg.departureDateTime ? seg.departureDateTime.split('T')[0] : '',
                departure_time: seg.departureDateTime ? seg.departureDateTime.split('T')[1]?.slice(0, 5) : '',
                arrival_date: seg.arrivalDateTime ? seg.arrivalDateTime.split('T')[0] : '',
                arrival_time: seg.arrivalDateTime ? seg.arrivalDateTime.split('T')[1]?.slice(0, 5) : '',
                flight_number: seg.flightNumber || '',
                airline: seg.airline?.name || '',
                airline_code: seg.airline?.code || '',
                duration: seg.durationMinutes ? `${Math.floor(seg.durationMinutes / 60)}h${String(seg.durationMinutes % 60).padStart(2, '0')}m` : '',
                aircraft: seg.aircraftCode || '',
                airport_change: airportChange || false,
                airport_change_detail: airportChange ? `${seg.destination.code} → ${nextSeg.origin.code}` : null,
              }
            })
          }
        }
      }

      // Busca imagem do destino
      let destImageUrl = null
      let destImageUrl2 = null
      const cityName = destinationCity || destination
      if (cityName) {
        try {
          const imgData = await api.getDestinationImages(cityName)
          destImageUrl = imgData?.destination_image_url || null
          destImageUrl2 = imgData?.destination_image_url_2 || null
        } catch (e) {}
      }

      const startDate = outbound[0].outbound?.departureDateTime?.split('T')[0] || ''
      const endDate   = ret.length > 0
        ? (ret[0].outbound?.departureDateTime?.split('T')[0] || startDate)
        : startDate

      const commonPayload = {
        client_name: contactName || 'Cliente',
        contact_id: contactId || undefined,
        destination: cityName || '',
        destination_image_url: destImageUrl,
        destination_image_url_2: destImageUrl2,
        start_date: startDate,
        end_date: endDate,
        adults, children, infants,
      }

      // ── Cotação pai — primeira opção de ida (+ volta se houver) ──────────
      const paiBlocks = [buildFlightBlock(outbound[0], 'ida')]
      if (ret.length > 0) paiBlocks.push(buildFlightBlock(ret[0], 'volta'))

      const paiResult = await api.createQuoteFromExtracted({ ...commonPayload, blocks: paiBlocks })
      if (!paiResult?.id) throw new Error('Falha ao criar cotação base')

      // ── Filhas — opções alternativas ──────────────────────────────────────
      // Combinações: cada voo de ida adicional × cada voo de volta adicional
      const altOutbound = outbound.slice(1)
      const altReturn   = ret.slice(1)

      const filhasPromises = []

      // Alternativas de ida (mantém primeiro voo de volta como referência)
      for (const offerOut of altOutbound) {
        const blocks = [buildFlightBlock(offerOut, 'ida')]
        if (ret.length > 0) blocks.push(buildFlightBlock(ret[0], 'volta'))
        filhasPromises.push(
          api.createQuoteFromExtracted({ ...commonPayload, blocks, parent_id: paiResult.id })
        )
      }

      // Alternativas de volta (mantém primeiro voo de ida)
      for (const offerRet of altReturn) {
        const blocks = [buildFlightBlock(outbound[0], 'ida'), buildFlightBlock(offerRet, 'volta')]
        filhasPromises.push(
          api.createQuoteFromExtracted({ ...commonPayload, blocks, parent_id: paiResult.id })
        )
      }

      // Cria filhas em paralelo — falha individual não desfaz o pai
      if (filhasPromises.length > 0) {
        await Promise.allSettled(filhasPromises)
      }

      navigate(`/app/cotacao/${paiResult.id}`)
    } catch (e) {
      console.error('Erro ao montar cotação:', e)
    } finally {
      setMountingQuote(false)
    }
  }, [cart, adults, children, infants, contactId, contactName, destination, destinationCity, navigate])

  const handleSearch = useCallback(async () => {
    if (!canSearch) return
    setLoading(true)
    setError(null)
    setOffers([])
    setCart({ outbound: [], return: [] })
    setSearched(true)

    try {
      const body = {
        origin, destination, departureDate, adults,
        ...(roundTrip && returnDate && { returnDate }),
        ...(children > 0 && { children }),
        ...(infants > 0 && { infants }),
      }
      const res = await fetch(`${API_URL}/public/flights/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      if (!res.ok) throw new Error(`Erro ${res.status}`)
      const data = await res.json()
      setOffers(data.offers || [])
    } catch (e) {
      setError('Erro ao buscar voos. Verifique a conexão e tente novamente.')
    } finally {
      setLoading(false)
    }
  }, [origin, destination, departureDate, returnDate, adults, children, infants, roundTrip, canSearch])

  const handleSwap = () => {
    const tmp = origin
    const tmpCity = originCity
    setOrigin(destination)
    setOriginCity(destinationCity)
    setDestination(tmp)
    setDestinationCity(tmpCity)
    setOffers([])
    setSearched(false)
    setCart({ outbound: [], return: [] })
  }

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#f8fafc', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <Sidebar activePage="aereo" />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Topbar */}
        <div style={{ background: colors.primary, padding: '16px 28px', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <Plane size={18} color={colors.accent} />
          <span style={{ color: 'white', fontSize: '15px', fontWeight: 600 }}>Busca de Voos</span>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Estação do Vendedor</span>
          <div style={{ flex: 1 }} />
          <button
            onClick={async () => {
              setShowBookings(true)
              setLoadingBookings(true)
              try {
                const r = await fetch(`${API_URL}/flight-bookings?limit=50`)
                const d = await r.json()
                setBookings(d.bookings || [])
              } catch { setBookings([]) }
              setLoadingBookings(false)
            }}
            style={{ padding: '6px 16px', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: 'white', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
          >
            Reservas
          </button>
        </div>

        {/* Formulário */}
        <div style={{ background: 'white', borderBottom: '1px solid #e2e8f0', padding: '16px 28px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>

            <div style={{ flex: '0 0 140px' }}>
              <div style={labelStyle}>Origem</div>
              <AirportInput value={origin} onChange={setOrigin} onCityChange={setOriginCity} placeholder="VDC ou cidade" />
            </div>

            <button onClick={handleSwap} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '9px', cursor: 'pointer', color: '#64748b', flexShrink: 0, marginBottom: '1px' }}>
              <ArrowLeftRight size={15} />
            </button>

            <div style={{ flex: '0 0 140px' }}>
              <div style={labelStyle}>Destino</div>
              <AirportInput value={destination} onChange={setDestination} onCityChange={setDestinationCity} placeholder="GRU ou cidade" />
            </div>

            <div style={{ flex: '0 0 130px' }}>
              <div style={labelStyle}>Ida</div>
              <input type="date" value={departureDate} onChange={e => setDepartureDate(e.target.value)} style={inputStyle} />
            </div>

            <div style={{ flex: '0 0 130px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                <span style={{ ...labelStyle, marginBottom: 0 }}>Volta</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: '#64748b', cursor: 'pointer' }}>
                  <input type="checkbox" checked={roundTrip} onChange={e => setRoundTrip(e.target.checked)} style={{ accentColor: colors.accent }} />
                  Ida e volta
                </label>
              </div>
              <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)}
                disabled={!roundTrip} style={{ ...inputStyle, opacity: roundTrip ? 1 : 0.4 }} />
            </div>

            {[
              { label: 'ADT', value: adults, set: setAdults, min: 1 },
              { label: 'CHD', value: children, set: setChildren, min: 0 },
              { label: 'INF', value: infants, set: setInfants, min: 0 },
            ].map(({ label, value, set, min }) => (
              <div key={label} style={{ flex: '0 0 72px' }}>
                <div style={labelStyle}>{label}</div>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: 'white' }}>
                  <button onClick={() => set(v => Math.max(min, v - 1))} style={{ padding: '8px 8px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '15px', lineHeight: 1 }}>−</button>
                  <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, color: colors.primary, fontSize: '14px' }}>{value}</div>
                  <button onClick={() => set(v => Math.min(9, v + 1))} style={{ padding: '8px 8px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '15px', lineHeight: 1 }}>+</button>
                </div>
              </div>
            ))}

            <button onClick={handleSearch} disabled={!canSearch || loading}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', border: 'none', borderRadius: '8px', background: canSearch && !loading ? colors.primary : '#e2e8f0', color: canSearch && !loading ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '14px', cursor: canSearch && !loading ? 'pointer' : 'not-allowed', flexShrink: 0 }}>
              {loading ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={15} />}
              Buscar
            </button>
          </div>
        </div>

        {/* Resultados */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>

          <FlightCart
            cart={cart}
            adults={adults + children}
            onRemove={handleRemoveFromCart}
            onMontar={handleMontarCotacao}
            mounting={mountingQuote}
            onReservar={() => setShowBookingModal(true)}
            roundTrip={roundTrip}
          />

          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#dc2626', fontSize: '13px' }}>
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
              <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', marginBottom: '10px' }} />
              <div style={{ fontSize: '13px' }}>Buscando voos...</div>
            </div>
          )}

          {!searched && !loading && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#94a3b8' }}>
              <Plane size={44} style={{ marginBottom: '12px', opacity: 0.25 }} />
              <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>Pronto para buscar</div>
              <div style={{ fontSize: '13px' }}>Preencha origem, destino e datas</div>
            </div>
          )}

          {searched && !loading && offers.length === 0 && !error && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#94a3b8' }}>
              <AlertCircle size={36} style={{ marginBottom: '12px', opacity: 0.35 }} />
              <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Nenhum voo encontrado</div>
              <div style={{ fontSize: '12px' }}>Tente outras datas ou verifique os códigos</div>
            </div>
          )}

          {!loading && offers.length > 0 && (
            <div>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px', fontWeight: 600 }}>
                {offers.length} opção{offers.length !== 1 ? 'ões' : ''} · {origin} → {destination}
                {roundTrip && returnDate ? ' · ida e volta' : ' · só ida'}
              </div>
              {offers.map((offer, i) => (
                <OfferCard
                  key={offer.offerId || i}
                  offer={offer}
                  selected={cart.outbound.some(o => o.offerId === offer.offerId) || cart.return.some(o => o.offerId === offer.offerId)}
                  onSelect={handleAddToCart}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Painel de Reservas */}
      {showBookings && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '12px', width: '100%', maxWidth: '780px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ background: colors.primary, padding: '16px 20px', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: 'white', fontSize: '14px', fontWeight: 700 }}>Reservas</span>
              <button onClick={() => setShowBookings(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: '18px' }}>✕</button>
            </div>
            <div style={{ padding: '16px' }}>
              {loadingBookings && <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>Carregando...</div>}
              {!loadingBookings && bookings.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>Nenhuma reserva encontrada</div>}
              {bookings.map(b => (
                <div key={b.id}
                  onClick={async () => {
                    try {
                      const r = await fetch(`${API_URL}/flight-bookings/${b.id}`)
                      const d = await r.json()
                      setSelectedBooking(d.booking || b)
                    } catch { setSelectedBooking(b) }
                    setShowBookings(false)
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: colors.accent, fontSize: '16px' }}>✈</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>
                      {b.booking_number} &nbsp;·&nbsp;
                      <span style={{ fontWeight: 400, color: '#64748b' }}>{b.origin} → {b.destination}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                      {b.locator && `Loc: ${b.locator}`}
                      {b.external_locator && ` · PNR: ${b.external_locator}`}
                      {b.departure_date && ` · ${new Date(b.departure_date).toLocaleDateString('pt-BR')}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: colors.primary }}>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(b.total_amount || 0)}
                    </div>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: b.status === 'confirmed' ? '#16a34a' : b.status === 'cancelled' ? '#dc2626' : '#92400e', marginTop: '2px' }}>
                      {b.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedBooking && <BookingDetail booking={selectedBooking} onClose={() => setSelectedBooking(null)} />}

      {showBookingModal && selectedOffer && (
        <BookingModal
          outbound={selectedOffer}
          returnFlight={selectedOffer.return ? selectedOffer : null}
          adults={adults}
          children={children}
          infants={infants}
          origin={origin}
          destination={destination}
          departureDate={departureDate}
          returnDate={returnDate}
          onClose={() => setShowBookingModal(false)}
          onSuccess={(result) => { console.log('[BookingModal] reserva criada:', result) }}
        />
      )}
    </div>
  )
}

const labelStyle = {
  fontSize: '10px', fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px'
}

const inputStyle = {
  width: '100%', padding: '9px 12px', fontSize: '14px',
  border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white',
  color: '#0f172a', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
}
